import { trackFullStoryEvent } from "@expert/monitoring";
import { useCallback, useEffect, useRef, useState } from "react";

import { getRootDispatcher } from "@/analytics";
import { sdkEventBus } from "@/sdk";

import type { OverlayPhase } from "./warmHandoffPhase";

const EXIT_DURATION_MS = 240;
const MIN_STANDBY_DURATION = 30;
const LIVE_AUTO_DISMISS_DURATION = 45;

interface UseWarmHandoffControllerResult {
    isDismissed: boolean;
    isClosing: boolean;
    handleContinue: () => void;
}

export function useWarmHandoffController(
    phase: OverlayPhase,
    activeTaskId: string | null,
    countdownDuration: number,
): UseWarmHandoffControllerResult {
    const [dismissedTaskId, setDismissedTaskId] = useState<string | null>(null);
    const [closingTaskId, setClosingTaskId] = useState<string | null>(null);
    const closingTaskIdRef = useRef(closingTaskId);
    // oxlint-disable-next-line react/refs
    closingTaskIdRef.current = closingTaskId;
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevTaskIdRef = useRef<string | null>(null);
    const activeTaskIdRef = useRef(activeTaskId);
    // oxlint-disable-next-line react/refs
    activeTaskIdRef.current = activeTaskId;
    const dismissReasonRef = useRef<string>("unknown");
    const liveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const beginClose = useCallback((taskId: string, reason: string) => {
        if (closeTimeoutRef.current) {
            if (closingTaskIdRef.current === taskId) return;
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        dismissReasonRef.current = reason;
        setClosingTaskId(taskId);
        closeTimeoutRef.current = setTimeout(() => {
            const eventData = { taskId, reason: dismissReasonRef.current };
            trackFullStoryEvent("WarmHandoffOverlayDismissed", eventData);
            void getRootDispatcher().dispatchBusinessEvent("WarmHandoffOverlayDismissed", eventData);
            setDismissedTaskId(taskId);
            setClosingTaskId(null);
            closeTimeoutRef.current = null;
        }, EXIT_DURATION_MS);
    }, []);

    useEffect(
        () => () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        },
        [],
    );

    useEffect(() => {
        const taskChanged = activeTaskId !== prevTaskIdRef.current;
        prevTaskIdRef.current = activeTaskId;
        if (taskChanged) {
            setDismissedTaskId(null);
        }
    }, [phase, activeTaskId]);

    useEffect(() => {
        const unsubDropped = sdkEventBus.on("call_customer_dropped", (droppedTask) => {
            if (droppedTask.id === activeTaskIdRef.current) {
                if (liveTimeoutRef.current) {
                    clearTimeout(liveTimeoutRef.current);
                    liveTimeoutRef.current = null;
                }
                beginClose(droppedTask.id, "customerDropped");
            }
        });

        return unsubDropped;
    }, [beginClose]);

    const standbyTimeout = Math.max(MIN_STANDBY_DURATION, countdownDuration + 5);
    useEffect(() => {
        if (phase !== "standby") return;
        const timeout = setTimeout(() => {
            if (activeTaskId) beginClose(activeTaskId, "timeout");
        }, standbyTimeout * 1000);
        return () => clearTimeout(timeout);
    }, [phase, activeTaskId, beginClose, standbyTimeout]);

    useEffect(() => {
        if (phase !== "live") return;
        liveTimeoutRef.current = setTimeout(() => {
            liveTimeoutRef.current = null;
            if (activeTaskId) beginClose(activeTaskId, "liveTimeout");
        }, LIVE_AUTO_DISMISS_DURATION * 1000);
        return () => {
            if (liveTimeoutRef.current) {
                clearTimeout(liveTimeoutRef.current);
                liveTimeoutRef.current = null;
            }
        };
    }, [phase, activeTaskId, beginClose]);

    const handleContinue = useCallback(() => {
        if (activeTaskId) beginClose(activeTaskId, "continue");
    }, [activeTaskId, beginClose]);

    return {
        isDismissed: dismissedTaskId === activeTaskId,
        isClosing: closingTaskId === activeTaskId,
        handleContinue,
    };
}
